import {
  Divider,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalProps,
  Text,
  Button,
} from '@chakra-ui/react';
import { PredictionsResult } from '@/model/predictions';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { formatDateToDateTimeDisplay } from '@/utils/formatDateToDateTimeDisplay';
import { useTranslation } from 'react-i18next';
import { PredictionsResultBreakdownRow } from './PredictionsResultBreakdownRow';

export interface PredictionsResultDetailModalProps extends Pick<ModalProps, 'isOpen' | 'onClose'> {
  isOpen: boolean;
  isLoading: boolean;
  item?: PredictionsResult;
  onClose: () => void;
}

const InfoField = ({ label, value }: { label: string; value: string }) => (
  <Flex direction='column' gap={1}>
    <Text fontSize='sm' color='gray.500'>
      {label}
    </Text>
    <Text>{value}</Text>
  </Flex>
);

export const PredictionsResultDetailModal = ({ isOpen, isLoading, item, onClose }: PredictionsResultDetailModalProps) => {
  const { t } = useTranslation();

  const breakdown = item?.breakdown ?? [];
  const maxPercentage = breakdown.reduce((max, row) => Math.max(max, row.percentage), 0);

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent minW={{ md: '640px' }}>
          <ModalHeader>
            {t('pages.application_predictions.components.predictions_result_detail_modal.predictions_result_detail_title')}
          </ModalHeader>
          <ModalCloseButton />

          {item ? (
            <ModalBody display='flex' flexDirection='column' gap={4}>
              <InfoField
                label={t(
                  'pages.application_predictions.components.predictions_result_detail_modal.predictions_result_detail_identifier',
                )}
                value={item.identifier}
              />
              <InfoField
                label={t(
                  'pages.application_predictions.components.predictions_result_detail_modal.predictions_result_detail_title_field',
                )}
                value={item.title}
              />
              <InfoField
                label={t(
                  'pages.application_predictions.components.predictions_result_detail_modal.predictions_result_detail_description',
                )}
                value={item.description}
              />

              <Flex gap={8}>
                <InfoField
                  label={t(
                    'pages.application_predictions.components.predictions_result_detail_modal.predictions_result_detail_start_date',
                  )}
                  value={formatDateToDateTimeDisplay(item.start_date)}
                />
                <InfoField
                  label={t(
                    'pages.application_predictions.components.predictions_result_detail_modal.predictions_result_detail_closing_date',
                  )}
                  value={item.closed_at ? formatDateToDateTimeDisplay(item.closed_at) : '-'}
                />
              </Flex>

              <Flex gap={8}>
                <InfoField
                  label={t(
                    'pages.application_predictions.components.predictions_result_detail_modal.predictions_result_detail_total_participants',
                  )}
                  value={String(item.total_participants)}
                />
                <InfoField
                  label={t(
                    'pages.application_predictions.components.predictions_result_detail_modal.predictions_result_detail_total_hits',
                  )}
                  value={`${item.total_hits} (${item.hit_percentage.toFixed(1)}%)`}
                />
              </Flex>

              <Divider />

              <Flex direction='column' gap={2}>
                <Text fontWeight='bold'>
                  {t(
                    'pages.application_predictions.components.predictions_result_detail_modal.predictions_result_detail_official_result',
                  )}
                </Text>
                <Text>
                  {item.option_a_name} {item.option_a_result ?? '-'} x {item.option_b_result ?? '-'} {item.option_b_name}
                </Text>
              </Flex>

              <Divider />

              <Flex direction='column' gap={2}>
                <Text fontWeight='bold'>
                  {t(
                    'pages.application_predictions.components.predictions_result_detail_modal.predictions_result_detail_predictions_list',
                  )}
                </Text>

                {breakdown.length === 0 ? (
                  <Text color='gray.500'>
                    {t(
                      'pages.application_predictions.components.predictions_result_detail_modal.predictions_result_detail_empty',
                    )}
                  </Text>
                ) : (
                  <Flex direction='column' gap={3}>
                    {breakdown.map((row, index) => (
                      <PredictionsResultBreakdownRow
                        key={`${row.option_a_result}-${row.option_b_result}`}
                        rank={index + 1}
                        item={row}
                        maxPercentage={maxPercentage}
                        optionAImageUrl={item.option_a_image_url}
                        optionBImageUrl={item.option_b_image_url}
                        isHit={
                          item.option_a_result !== null &&
                          item.option_b_result !== null &&
                          row.option_a_result === item.option_a_result &&
                          row.option_b_result === item.option_b_result
                        }
                      />
                    ))}
                  </Flex>
                )}
              </Flex>
            </ModalBody>
          ) : (
            <ModalBody minH='200px' />
          )}

          <ModalFooter>
            <Button variant='outlined' color='gray.600' onClick={onClose}>
              {t('pages.application_predictions.components.predictions_result_detail_modal.predictions_result_detail_close')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <CenteredSpinner isLoading={isLoading} />
    </>
  );
};
